import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { Select, Option } from '../../../../components/inputs/Select';
import { BaseInputProps } from './BaseInput';
import { PropertyValueData, AnnotationType } from '../../types';
import useT from '../../../../translation/src/hooks/useT';
import { useFlatAnnotationList } from '../../../../hooks/potree/useFlatAnnotationList';

const TranslatedLabel: React.FC<{ label: string }> = ({ label }) => {
  const translated = useT(label, { swc: true });
  return <span>{translated || label}</span>;
};

const TranslatedOption: React.FC<{
  value: PropertyValueData;
  label: string;
}> = ({ value, label }) => {
  const translatedLabel = useT(label, { swc: true });
  return <Option value={value}>{translatedLabel || label}</Option>;
};

const MIXED_SENTINEL = '__mixed__';

export const SelectInput: React.FC<BaseInputProps<PropertyValueData>> = ({ instance, onChange, disabled = false, className }) => {
  const { config, value } = instance;
  const mixedText = useT('mixed', { swc: true });
  const { allSelectedAnnotations } = useFlatAnnotationList();

  const handleChange = useCallback(
    (event: { target: { value: PropertyValueData; name: string } }) => {
      // Ignore re-selection of the mixed sentinel — it's a display-only entry
      if (event.target.value === MIXED_SENTINEL) return;
      onChange(event.target.value);
    },
    [onChange],
  );

  // Determine the annotation type for this instance to filter options
  const annotationType = useMemo((): AnnotationType | undefined => {
    if (!instance.objectIdentifier) return undefined;
    const annotation = allSelectedAnnotations.find((a) => a.identifier === instance.objectIdentifier);
    return annotation?.type as AnnotationType | undefined;
  }, [instance.objectIdentifier, allSelectedAnnotations]);

  const options = useMemo(
    () =>
      (config.validation?.options ?? []).filter((option) => {
        if (option.disabled) return false;
        // Filter by annotation type if the option specifies it
        if (option.annotationTypes && option.annotationTypes.length > 0) {
          if (!annotationType) return true; // No context — show all
          return option.annotationTypes.includes(annotationType);
        }
        return true;
      }),
    [config.validation?.options, annotationType],
  );

  const renderSelectedValue = useCallback(
    (selectedValue: string) => {
      if (instance.isMixed) return <span>{mixedText}</span>;
      // Disabled options aren't offered but still name a value set earlier (an archived organisation door type).
      const selectedOption = options.find((opt) => opt.value === selectedValue) ?? config.validation?.options?.find((opt) => opt.value === selectedValue);
      if (!selectedOption) return selectedValue;
      return <TranslatedLabel label={typeof selectedOption.label === 'string' ? selectedOption.label : ''} />;
    },
    [options, config.validation?.options, instance.isMixed, mixedText],
  );

  // When mixed, use a sentinel value so the real options don't show as selected
  const displayValue = instance.isMixed ? MIXED_SENTINEL : (value as string);

  return (
    <div className={classNames('relative flex-1', className)}>
      <Select value={displayValue} onChange={handleChange} disabled={disabled || config.disabled} mixed={instance.isMixed} renderSelectedValue={renderSelectedValue} className="w-full" centered>
        {instance.isMixed && (
          <Option key="mixed" value={MIXED_SENTINEL}>
            {mixedText}
          </Option>
        )}
        {options.map((option) => (
          <TranslatedOption key={typeof option.value === 'object' ? JSON.stringify(option.value) : String(option.value)} value={option.value} label={typeof option.label === 'string' ? option.label : ''} />
        ))}
      </Select>
    </div>
  );
};
